import {
    useState,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type ISliderThumbProps from "./type";
import type {
    SliderThumbAccessibilityActionEventType,
    SliderThumbKeyboardEventType,
    SliderThumbWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    getInteractionStateName,
    useInteractionState
} from "../../../../helpers/interaction";
import Text from "../../../text";

const SliderThumb: FC<ISliderThumbProps> = ({
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    onKeyDown: onKeyDownProp,
    "aria-label": ariaLabel,
    onFocus: onFocusProp,
    onKeyUp: onKeyUpProp,
    onBlur: onBlurProp,
    currentMeasures,
    tipDistance = 0,
    currentColors,
    customTheme,
    orientation,
    pointerType,
    isDisabled,
    isPressed,
    valueText,
    valueMax,
    valueMin,
    valueNow,
    tipText,
    viewRef,
    offset,
    onStep,
    index
}) => {
    const {
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const [
        isHovered,
        setIsHovered
    ] = useState<boolean>(false);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        onFocus: () => onFocusProp?.(index),
        onBlur: () => onBlurProp?.(index),
        isDisabled
    });

    const {
        container: containerDynamicStyle,
        bubble: bubbleDynamicStyle,
        tip: tipDynamicStyle
    } = useStyles({
        currentMeasures,
        isFocusVisible,
        currentColors,
        shapeConfigs,
        touchTargets,
        orientation,
        pointerType,
        shadowAlpha,
        tipDistance,
        elevations,
        isDisabled,
        isHovered,
        isPressed,
        radiuses,
        borders,
        colors,
        spaces,
        offset
    });

    const onKeyDown = (event: SliderThumbKeyboardEventType) => {
        onKeyDownProp?.(index, event);
    };

    const onKeyUp = () => {
        onKeyUpProp?.(index);
    };

    const onPointerEnter = () => {
        setIsHovered(true);
    };

    const onPointerLeave = () => {
        setIsHovered(false);
    };

    const onAccessibilityAction = ({
        nativeEvent
    }: SliderThumbAccessibilityActionEventType) => {
        if(nativeEvent.actionName === "increment") {
            onStep?.(index, 1);

            return;
        }

        if(nativeEvent.actionName === "decrement") {
            onStep?.(index, -1);
        }
    };

    const webProps: SliderThumbWebPropsType = Platform.OS === "web" ? {
        dataSet: {
            [INTERACTION_STATE_DATA_KEY]: getInteractionStateName({
                isHovered: isHovered && pointerType === "fine",
                isFocusVisible,
                isDisabled,
                isPressed
            })
        },
        "aria-orientation": orientation,
        onKeyDown,
        onKeyUp
    } : {};

    const renderTip = () => {
        if(!tipText || isDisabled) {
            return null;
        }

        return <View
            style={[
                stylesheet.tip,
                orientation === "vertical" ? stylesheet.verticalTip : stylesheet.horizontalTip,
                tipDynamicStyle
            ]}
            aria-hidden={true}
        >
            <View
                style={[
                    stylesheet.bubble,
                    bubbleDynamicStyle
                ]}
            >
                <Text
                    style={stylesheet.bubbleText}
                    customTheme={customTheme}
                    variant="labelSmallSize"
                    color="contrastHigh"
                    numberOfLines={1}
                >
                    {tipText}
                </Text>
            </View>
        </View>;
    };

    return <View
        {...webProps}
        onAccessibilityAction={onAccessibilityAction}
        style={[
            stylesheet.container,
            isPressed ? stylesheet.pressed : null,
            isDisabled ? stylesheet.disabled : null,
            containerDynamicStyle
        ]}
        onFocus={interactionHandlers.onFocus}
        onBlur={interactionHandlers.onBlur}
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        onPointerLeave={onPointerLeave}
        onPointerEnter={onPointerEnter}
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        aria-valuetext={valueText}
        accessibilityActions={[
            {
                name: "increment"
            },
            {
                name: "decrement"
            }
        ]}
        aria-valuemin={valueMin}
        aria-valuemax={valueMax}
        aria-valuenow={valueNow}
        focusable={!isDisabled}
        aria-label={ariaLabel}
        accessible={true}
        role="slider"
        ref={viewRef}
    >
        {renderTip()}
    </View>;
};
export default SliderThumb;
